<template>
  <div id="app">
    <!-- Navigation -->
    <nav class="nav" :class="{ 'scrolled': isScrolled }">
      <div class="container nav-content">
        <div class="logo-container">
          <svg class="logo-svg" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path d="M 15 10 L 25 10 L 35 25 L 25 40 L 15 40 L 25 25 Z" fill="url(#logoGradient)"/>
            <circle cx="40" cy="15" r="3" fill="url(#logoGradient)"/>
            <circle cx="40" cy="35" r="3" fill="url(#logoGradient)"/>
          </svg>
          <div class="logo-text">
            <span class="logo-name">Nick Volkmann</span>
            <span class="logo-subtitle">IT-Beratung</span>
          </div>
        </div>
        <div class="nav-links">
          <a href="#home" @click.prevent="scrollTo('home')">Start</a>
          <a href="#services" @click.prevent="scrollTo('services')">Leistungen</a>
          <a href="#portfolio" @click.prevent="scrollTo('portfolio')">Referenzen</a>
          <a href="#contact" @click.prevent="scrollTo('contact')">Kontakt</a>
          <!-- Login Button -->
          <router-link to="/login" class="login-btn" aria-label="Zum Login">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M10 17l5-5-5-5"/><path d="M15 12H3"/><path d="M21 3h-8a2 2 0 0 0-2 2v2"/>
              <path d="M11 17v2a2 2 0 0 0 2 2h8"/>
            </svg>
            <span>Login</span>
          </router-link>
        </div>
        <button class="mobile-menu-btn" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      <div class="mobile-menu" :class="{ 'active': mobileMenuOpen }">
        <a href="#home" @click.prevent="scrollTo('home'); closeMobileMenu()">Start</a>
        <a href="#services" @click.prevent="scrollTo('services'); closeMobileMenu()">Leistungen</a>
        <a href="#portfolio" @click.prevent="scrollTo('portfolio'); closeMobileMenu()">Referenzen</a>
        <a href="#contact" @click.prevent="scrollTo('contact'); closeMobileMenu()">Kontakt</a>
      </div>
    </nav>

    <!-- Hero Section -->
    <section id="home" class="hero">
      <div class="hero-background">
        <div class="grid-overlay"></div>
      </div>
      <div class="container hero-content">
        <div class="hero-text">
          <h1 class="hero-title">
            <span class="title-line">Professionelle</span>
            <span class="title-line gradient-text">Webentwicklung</span>
            <span class="title-line">mit Komplettservice</span>
          </h1>
          <p class="hero-subtitle">Von der strategischen Konzeption bis zum erfolgreichen Go-Live – inklusive intelligenter Automatisierungslösungen für Ihr Business</p>
          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-number">{{ projectCount }}+</div>
              <div class="stat-label">Projekte</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">100%</div>
              <div class="stat-label">Kundenzufriedenheit</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">24/7</div>
              <div class="stat-label">Support</div>
            </div>
          </div>
          <button class="cta-button" @click="scrollTo('contact')">
            <span>Projekt anfragen</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
        <div class="hero-visual">
          <div class="floating-card card-1">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <path d="M3 9h18"/>
              <path d="M9 21V9"/>
            </svg>
            <span>Responsive Design</span>
          </div>
          <div class="floating-card card-2">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            <span>Vue.js</span>
          </div>
          <div class="floating-card card-3">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            <span>Performance</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Interactive Stats Widget -->
    <section class="stats-widget">
      <div class="container">
        <div class="widget-card">
          <h3>Live Performance Metriken</h3>
          <div class="metrics-grid">
            <div class="metric">
              <div class="metric-value">{{ currentTime }}</div>
              <div class="metric-label">Aktuelle Zeit</div>
            </div>
            <div class="metric">
              <div class="metric-value">{{ loadTime }}ms</div>
              <div class="metric-label">Ladezeit</div>
            </div>
            <div class="metric">
              <div class="metric-value">99/100</div>
              <div class="metric-label">Performance Score</div>
            </div>
          </div>
          <div class="progress-bars">
            <div class="progress-item">
              <span>SEO Optimierung</span>
              <div class="progress-bar"><div class="progress-fill" style="width: 95%"></div></div>
            </div>
            <div class="progress-item">
              <span>Accessibility</span>
              <div class="progress-bar"><div class="progress-fill" style="width: 100%"></div></div>
            </div>
            <div class="progress-item">
              <span>Best Practices</span>
              <div class="progress-bar"><div class="progress-fill" style="width: 98%"></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Alleinstellungsmerkmal -->
    <section class="usp">
      <div class="container">
        <div class="section-header">
          <span class="section-tag">Ihr Vorteil</span>
          <h2 class="section-title">Komplettlösung aus einer Hand</h2>
          <p class="section-subtitle">Wirtschaftsinformatik-Expertise trifft auf moderne Webtechnologien</p>
        </div>
        
        <div class="usp-grid">
          <div class="usp-card">
            <div class="usp-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h3>Design & Entwicklung</h3>
            <p>Moderne, responsive Webanwendungen mit Vue.js – optimiert für Performance, Skalierbarkeit und User Experience</p>
          </div>
          
          <div class="usp-card">
            <div class="usp-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h3>Hosting & Go-Live</h3>
            <p>Professionelles Hosting auf High-Performance-Servern mit SSL, CDN und automatischen Backups – für maximale Verfügbarkeit</p>
          </div>
          
          <div class="usp-card">
            <div class="usp-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </div>
            <h3>Wartung & Support</h3>
            <p>Kontinuierliche Betreuung, Security Updates und 24/7 technischer Support für langfristigen Erfolg Ihrer Webpräsenz</p>
          </div>
          
          <div class="usp-card featured">
            <div class="featured-badge">Premium Feature</div>
            <div class="usp-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <h3>Intelligentes Terminmanagement</h3>
            <p>Vollautomatisierte Buchungssysteme mit Multi-Channel-Kommunikation, Payment-Integration und KI-gestützter Terminoptimierung</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Detail -->
    <section id="services" class="services">
      <div class="container">
        <div class="section-header">
          <span class="section-tag">Funktionen</span>
          <h2 class="section-title">Enterprise Terminbuchungssystem</h2>
        </div>
        <div class="services-container">
          <div class="services-visual">
            <div class="service-demo-card">
              <div class="demo-header">
                <div class="demo-dot"></div>
                <div class="demo-dot"></div>
                <div class="demo-dot"></div>
              </div>
              <div class="demo-content">
                <div class="demo-item" v-for="(item, index) in demoItems" :key="index" :style="{ animationDelay: index * 0.2 + 's' }">
                  <div class="demo-icon">✓</div>
                  <div class="demo-text">{{ item }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="services-list">
            <div class="service-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>
                <h4>Online-Terminbuchung & -planung</h4>
                <p>Intuitive Benutzeroberfläche mit Echtzeit-Verfügbarkeit und Kalender-Synchronisation</p>
              </div>
            </div>
            <div class="service-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>
                <h4>Automatische Email-Benachrichtigungen</h4>
                <p>Professionelle Transaktionsmails mit individuellen Templates und Branding</p>
              </div>
            </div>
            <div class="service-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>
                <h4>WhatsApp Terminerinnerungen</h4>
                <p>Automatisierte Erinnerungen via WhatsApp Business API für maximale Erreichbarkeit</p>
              </div>
            </div>
            <div class="service-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>
                <h4>PayPal & Stripe Integration</h4>
                <p>Sichere Online-Zahlungen mit internationalen Zahlungsmethoden und Rechnungsstellung</p>
              </div>
            </div>
            <div class="service-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <div>
                <h4>Telefonhotline für Buchungen</h4>
                <p>VoIP-Integration für telefonische Terminvereinbarungen mit CRM-Anbindung</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Interactive Tech Stack Widget -->
    <section class="tech-stack">
      <div class="container">
        <div class="section-header">
          <span class="section-tag">Technologie</span>
          <h2 class="section-title">Moderne Tech-Stack</h2>
        </div>
        <div class="tech-grid">
          <div class="tech-item" v-for="tech in technologies" :key="tech.name" @mouseenter="activeTech = tech.name" @mouseleave="activeTech = null">
            <div class="tech-logo" :class="{ active: activeTech === tech.name }">
              <div class="tech-icon">{{ tech.icon }}</div>
            </div>
            <div class="tech-name">{{ tech.name }}</div>
            <div class="tech-desc">{{ tech.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Portfolio -->
    <section id="portfolio" class="portfolio">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Referenzprojekte</h2>
          <p class="section-subtitle">Erfolgreiche Digitalisierung für zufriedene Geschäftskunden</p>
        </div>
        
        <div class="portfolio-grid">
          <div v-for="project in projects" :key="project.id" class="portfolio-card" @mouseenter="hoveredProject = project.id" @mouseleave="hoveredProject = null">
            <div class="portfolio-image" :style="{ backgroundImage: `linear-gradient(135deg, ${project.color1} 0%, ${project.color2} 100%)` }">
              <div class="portfolio-overlay" :class="{ visible: hoveredProject === project.id }">
                <a :href="project.url" target="_blank" rel="noopener noreferrer" class="view-button">
                  <span>Website besuchen</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </div>
              <div class="portfolio-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
            </div>
            <div class="portfolio-info">
              <div class="portfolio-meta">
                <span class="portfolio-category">{{ project.category }}</span>
                <span class="portfolio-year">{{ project.year }}</span>
              </div>
              <h3>{{ project.title }}</h3>
              <p>{{ project.description }}</p>
              <div class="portfolio-tags">
                <span v-for="tag in project.tags" :key="tag" class="tag">{{ tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Form -->
    <section id="contact" class="contact">
      <div class="container">
        <div class="contact-wrapper">
          <div class="contact-info">
            <div class="section-header left">
              <span class="section-tag">Kontakt</span>
              <h2 class="section-title">Lassen Sie uns über Ihr Projekt sprechen</h2>
              <p class="section-subtitle">Kostenlose Erstberatung und unverbindliches Angebot innerhalb von 24 Stunden</p>
            </div>
            
            <div class="contact-details">
              <div class="contact-detail-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <h4>Standort</h4>
                  <p>Dresden, Sachsen</p>
                </div>
              </div>
              
              <div class="contact-detail-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                <div>
                  <h4>Email</h4>
                  <p>info@nick-volkmann.de</p>
                </div>
              </div>
              
              <div class="contact-detail-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <div>
                  <h4>Telefon</h4>
                  <P>0176/20074278</P>
                </div>
              </div>
            </div>
            
            <div class="trust-badges">
              <div class="trust-badge">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>DSGVO-konform</span>
              </div>
              <div class="trust-badge">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <span>Festpreisgarantie</span>
              </div>
            </div>
          </div>
           <form @submit.prevent="handleSubmit" class="contact-form">
            <div class="form-grid">
              <div class="form-group">
                <label for="name">Name *</label>
                <input type="text" id="name" v-model="form.name" required placeholder="Ihr vollständiger Name">
              </div>
              
              <div class="form-group">
                <label for="company">Unternehmen *</label>
                <input type="text" id="company" v-model="form.company" required placeholder="Ihre Firma">
              </div>
              
              <div class="form-group">
                <label for="email">Email *</label>
                <input type="email" id="email" v-model="form.email" required placeholder="ihre@email.de">
              </div>
              
              <div class="form-group">
                <label for="phone">Telefon</label>
                <input type="tel" id="phone" v-model="form.phone" placeholder="+49 123 456789">
              </div>
            </div>
            
            <div class="form-group">
              <label for="service">Interessiert an</label>
              <select id="service" v-model="form.service">
                <option value="">Bitte wählen Sie eine Option</option>
                <option value="website">Website Design & Entwicklung</option>
                <option value="booking">Terminbuchungssystem</option>
                <option value="complete">Komplettpaket (Website + Terminbuchung)</option>
                <option value="maintenance">Wartung & Support</option>
                <option value="consulting">IT-Beratung</option>
                <option value="other">Sonstiges</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="budget">Budget (optional)</label>
              <select id="budget" v-model="form.budget">
                <option value="">Bitte wählen</option>
                <option value="5k">Bis 5.000 €</option>
                <option value="10k">5.000 - 10.000 €</option>
                <option value="20k">10.000 - 20.000 €</option>
                <option value="20k+">Über 20.000 €</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="message">Projektbeschreibung *</label>
              <textarea id="message" v-model="form.message" rows="5" required placeholder="Beschreiben Sie Ihr Projekt, Ihre Ziele und spezielle Anforderungen..."></textarea>
            </div>
            
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.privacy" required>
                <span class="checkbox-custom"></span>
                <span>Ich habe die Datenschutzerklärung gelesen und akzeptiere diese *</span>
              </label>
            </div>
            
            <button type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Anfrage senden</span>
              <span v-else>Wird gesendet...</span>
              <svg v-if="!isSubmitting" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
            
            <div v-if="submitMessage" class="submit-message" :class="submitStatus">
              <svg v-if="submitStatus === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>{{ submitMessage }}</span>
            </div>
          </form>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-brand">
            <svg class="footer-logo" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="footerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
                </linearGradient>
              </defs>
              <path d="M 15 10 L 25 10 L 35 25 L 25 40 L 15 40 L 25 25 Z" fill="url(#footerGradient)"/>
              <circle cx="40" cy="15" r="3" fill="url(#footerGradient)"/>
              <circle cx="40" cy="35" r="3" fill="url(#footerGradient)"/>
            </svg>
            <div>
              <div class="footer-brand-name">Nick Volkmann</div>
              <div class="footer-brand-subtitle">IT-Beratung</div>
            </div>
          </div>
          
          <div class="footer-links">
            <div class="footer-column">
              <h4>Services</h4>
              <a href="#services">Webentwicklung</a>
              <a href="#services">Terminbuchung</a>
              <a href="#services">Hosting & Wartung</a>
              <a href="#services">IT-Beratung</a>
            </div>
            
            <div class="footer-column">
              <h4>Unternehmen</h4>
              <a href="#portfolio">Referenzen</a>
              <a href="#contact">Kontakt</a>
            </div>
            
            <div class="footer-column">
              <h4>Rechtliches</h4>
              <router-link to="/impressum">Impressum</router-link>
              <router-link to="/datenschutz">Datenschutz</router-link>
            </div>
          </div>
        </div>
        
        <div class="footer-bottom">
          <p>&copy; 2025 Nick Volkmann - IT-Beratung. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { submitContactForm } from '@/apiService';

export default {
  name: 'App',
  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      hoveredProject: null,
      activeTech: null,
      projectCount: 2,
      loadTime: 342,
      currentTime: '',
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        service: '',
        budget: '',
        message: '',
        privacy: false
      },
      isSubmitting: false,
      submitMessage: '',
      submitStatus: '',
      demoItems: [
        'Termin bestätigt um 14:30',
        'Email-Benachrichtigung gesendet',
        'WhatsApp-Erinnerung geplant',
        'Zahlung über PayPal erfolgreich'
      ],
      technologies: [
        { name: 'Vue.js', icon: 'V', desc: 'Progressive Framework' },
        { name: 'Django', icon: 'DJ', desc: 'Python Backend' },
        { name: 'Node.js', icon: 'N', desc: 'Backend Runtime' },
        { name: 'TypeScript', icon: 'TS', desc: 'Type Safety' },
        { name: 'PostgreSQL', icon: 'DB', desc: 'Database' },
        { name: 'Docker', icon: 'D', desc: 'Containerization' },
        { name: 'AWS', icon: 'A', desc: 'Cloud Infrastructure' }
      ],
      projects: [
        {
          id: 1,
          title: 'Salongstudio',
          category: 'Beauty & Lifestyle',
          year: '2025',
          url: 'https://salongstudio.de/',
          description: 'Professionelle Beauty-Plattform mit vollintegriertem Terminbuchungssystem, Online-Zahlungen und automatischer Kundenkommunikation für einen modernen Friseursalon',
          tags: ['Design', 'Terminbuchung', 'E-Commerce', 'WhatsApp', "Telefonhotline"],
          color1: '#f093fb',
          color2: '#f5576c',
        },
        {
          id: 2,
          title: 'Pimp My Hair',
          category: 'Beauty & Lifestyle',
          year: '2025',
          url: 'https://pimpmyhair.de/',
          description: 'Stilvolle und intuitive Website mit Service-Katalog, Stylist-Auswahl, automatischen Erinnerungen und seamless Booking-Experience',
          tags: ['Design','Terminbuchung', 'PayPal'],
          color1: '#fa709a',
          color2: '#fee140',
        },
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.updateTime();
    setInterval(this.updateTime, 1000);
    this.animateStats();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    scrollTo(section) {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    },
    animateStats() {
      setInterval(() => {
        this.visitorCount += Math.floor(Math.random() * 3);
      }, 5000);
    },
    async handleSubmit() {
      this.isSubmitting = true;
      this.submitMessage = '';
      
      setTimeout(() => {
        this.submitMessage = 'Vielen Dank für Ihre Anfrage! Ich melde mich innerhalb von 24 Stunden bei Ihnen mit einem unverbindlichen Angebot.';
        this.submitStatus = 'success';
        this.isSubmitting = false;
        
        this.form = {
          name: '',
          company: '',
          email: '',
          phone: '',
          service: '',
          budget: '',
          message: '',
          privacy: false
        };
        
        setTimeout(() => {
          this.submitMessage = '';
        }, 7000);
      }, 1500);
    },
    async handleSubmit() {
      this.isSubmitting = true;
      this.submitMessage = '';
      this.submitStatus = '';
      
      try {
        // 📡 API-Aufruf über submitContactForm aus apiService.js
        const response = await submitContactForm({
          name: this.form.name,
          company: this.form.company,
          email: this.form.email,
          phone: this.form.phone,
          service: this.form.service,
          budget: this.form.budget,
          message: this.form.message,
          privacy: this.form.privacy
        });
        
        // ✅ Erfolg
        if (response.success) {
          this.submitMessage = response.message || 'Vielen Dank für Ihre Anfrage! Ich melde mich innerhalb von 24 Stunden bei Ihnen.';
          this.submitStatus = 'success';
          
          // 🔄 Formular zurücksetzen
          this.form = {
            name: '',
            company: '',
            email: '',
            phone: '',
            service: '',
            budget: '',
            message: '',
            privacy: false
          };
          
          // 🕐 Erfolgsmeldung nach 7 Sekunden ausblenden
          setTimeout(() => {
            this.submitMessage = '';
            this.submitStatus = '';
          }, 7000);
        }
        
      } catch (error) {
        // ❌ Fehlerbehandlung
        console.error('🚨 Fehler beim Absenden:', error);
        
        // Spezifische Fehlermeldungen basierend auf HTTP-Status
        if (error.response?.data?.errors) {
          // Django Validierungsfehler
          const errors = error.response.data.errors;
          this.submitMessage = Object.values(errors).flat().join(', ');
        } else if (error.response?.status === 429) {
          // Rate Limit erreicht
          this.submitMessage = 'Zu viele Anfragen. Bitte versuchen Sie es in einer Stunde erneut.';
        } else if (error.response?.status >= 500) {
          // Server Error
          this.submitMessage = 'Serverfehler. Bitte versuchen Sie es später erneut oder kontaktieren Sie mich telefonisch.';
        } else if (error.message.includes('Network Error')) {
          // Netzwerkfehler
          this.submitMessage = 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
        } else {
          // Allgemeiner Fehler
          this.submitMessage = 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut.';
        }
        
        this.submitStatus = 'error';
        
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --primary: #667eea;
  --secondary: #764ba2;
  --dark: #1a202c;
  --gray: #4a5568;
  --light-gray: #e2e8f0;
  --success: #10b981;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  color: var(--dark);
  background: #fff;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Navigation */
.nav {
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  z-index: 1000;
  transition: all 0.3s ease;
}

.nav.scrolled {
  box-shadow: 0 2px 30px rgba(0, 0, 0, 0.08);
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-svg {
  width: 40px;
  height: 40px;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.logo-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--dark);
  line-height: 1.2;
}

.logo-subtitle {
  font-size: 12px;
  color: var(--gray);
  font-weight: 500;
}

.nav-links {
  display: flex;
  gap: 40px;
  align-items: center;
}

.nav-links a {
  color: var(--dark);
  text-decoration: none;
  font-weight: 500;
  font-size: 15px;
  position: relative;
  transition: color 0.3s;
}

.nav-links a::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
  transition: width 0.3s;
}

.nav-links a:hover {
  color: var(--primary);
}

.nav-links a:hover::after {
  width: 100%;
}

.mobile-menu-btn {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}

.mobile-menu-btn span {
  width: 25px;
  height: 2px;
  background: var(--dark);
  transition: all 0.3s;
}

.mobile-menu {
  display: none;
  flex-direction: column;
  background: white;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.mobile-menu a {
  padding: 15px 0;
  color: var(--dark);
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid var(--light-gray);
}

.mobile-menu.active {
  display: flex;
}

/* Desktop Login Button */
.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 12px;
  text-decoration: none;
  position: relative;
  transition: all 0.25s ease;
  /* Gradient-Border + weißer Hintergrund */
  border: 2px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, var(--primary), var(--secondary)) border-box;
  color: var(--primary);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.login-btn:hover,
.login-btn:focus-visible {
  /* Vollflächiger Gradient beim Hover */
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(102, 126, 234, 0.35);
}

.login-btn svg {
  stroke: currentColor;
}

/* Mobile-Menü Login Button */
.mobile-login {
  margin-top: 12px;
  display: block;
  text-align: center;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 700;
  text-decoration: none;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: #fff;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.35);
}

.mobile-login:active {
  transform: translateY(1px);
}

/* Hero Section */
.hero {
  position: relative;
  margin-top: 80px;
  padding: 120px 0 100px;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: -1;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.hero-content {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: center;
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
}

.title-line {
  display: block;
}

.gradient-text {
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.8));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 18px;
  margin-bottom: 40px;
  opacity: 0.95;
  line-height: 1.7;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 40px;
  padding: 30px 0;
  border-top: 1px solid rgba(255,255,255,0.2);
  border-bottom: 1px solid rgba(255,255,255,0.2);
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: var(--primary);
  border: none;
  padding: 18px 40px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
}

.hero-visual {
  position: relative;
  height: 400px;
}

.floating-card {
  position: absolute;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px 24px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-weight: 600;
  animation: float 3s ease-in-out infinite;
}

.floating-card svg {
  flex-shrink: 0;
}

.card-1 {
  top: 20px;
  left: 20px;
  animation-delay: 0s;
}

.card-2 {
  top: 140px;
  right: 40px;
  animation-delay: 1s;
}

.card-3 {
  bottom: 60px;
  left: 60px;
  animation-delay: 2s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

/* Stats Widget */
.stats-widget {
  padding: 60px 0;
  background: #f8fafc;
}

.widget-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
}

.widget-card h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
  color: var(--dark);
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.metric {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}

.metric-value {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 5px;
}

.metric-label {
  font-size: 14px;
  opacity: 0.95;
}

.progress-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.progress-item span {
  min-width: 150px;
  font-weight: 600;
  color: var(--dark);
}

.progress-bar {
  flex: 1;
  height: 12px;
  background: var(--light-gray);
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
  border-radius: 10px;
  animation: progressFill 2s ease-out;
}

@keyframes progressFill {
  from { width: 0; }
}

/* Sections */
.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-header.left {
  text-align: left;
}

.section-tag {
  display: inline-block;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 42px;
  font-weight: 800;
  margin-bottom: 16px;
  color: var(--dark);
}

.section-subtitle {
  font-size: 18px;
  color: var(--gray);
  max-width: 600px;
  margin: 0 auto;
}

.section-header.left .section-subtitle {
  margin: 0;
}

/* USP Section */
.usp {
  padding: 100px 0;
}

.usp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
}

.usp-card {
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  position: relative;
}

.usp-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 50px rgba(0, 0, 0, 0.15);
}

.usp-card.featured {
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
}

.featured-badge {
  position: absolute;
  top: -12px;
  right: 20px;
  background: #fff;
  color: var(--primary);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.usp-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.usp-card.featured .usp-icon {
  background: rgba(255, 255, 255, 0.2);
}

.usp-icon svg {
  stroke-width: 2;
}

.usp-card h3 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
}

.usp-card p {
  line-height: 1.7;
  opacity: 0.9;
}

/* Services Section */
.services {
  padding: 100px 0;
  background: #f8fafc;
}

.services-container {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 60px;
  align-items: center;
}

.service-demo-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.12);
}

.demo-header {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.demo-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--light-gray);
}

.demo-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.demo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 12px;
  animation: slideIn 0.5s ease-out forwards;
  opacity: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.demo-icon {
  width: 28px;
  height: 28px;
  background: var(--success);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.demo-text {
  font-weight: 500;
  color: var(--dark);
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.service-item {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s;
}

.service-item:hover {
  transform: translateX(8px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.service-item svg {
  flex-shrink: 0;
  stroke: var(--success);
}

.service-item h4 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
  color: var(--dark);
}

.service-item p {
  color: var(--gray);
  font-size: 14px;
  line-height: 1.6;
}

/* Tech Stack */
.tech-stack {
  padding: 100px 0;
  background: var(--dark);
  color: white;
}

.tech-stack .section-tag {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.tech-stack .section-title {
  color: white;
}

.tech-stack .section-subtitle {
  color: rgba(255, 255, 255, 0.7);
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 30px;
}

.tech-item {
  text-align: center;
  cursor: pointer;
}

.tech-logo {
  width: 100px;
  height: 100px;
  margin: 0 auto 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.tech-logo:hover,
.tech-logo.active {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border-color: transparent;
  transform: translateY(-8px) scale(1.05);
}

.tech-icon {
  font-size: 32px;
  font-weight: 800;
  font-family: 'Courier New', monospace;
}

.tech-name {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
}

.tech-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

/* Portfolio */
.portfolio {
  padding: 100px 0;
}

.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
}

.portfolio-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.portfolio-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 50px rgba(0, 0, 0, 0.15);
}

.portfolio-image {
  height: 280px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.portfolio-icon {
  opacity: 0.3;
}

.portfolio-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.portfolio-overlay.visible {
  opacity: 1;
}

.view-button {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: var(--dark);
  border: none;
  padding: 14px 30px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  text-decoration: none;
}

.view-button:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);
}

.portfolio-info {
  padding: 30px;
}

.portfolio-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.portfolio-category {
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.portfolio-year {
  font-size: 13px;
  color: var(--gray);
  font-weight: 500;
}

.portfolio-info h3 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--dark);
}

.portfolio-info p {
  color: var(--gray);
  margin-bottom: 20px;
  line-height: 1.7;
  font-size: 15px;
}

.portfolio-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.tag {
  background: #f1f5f9;
  color: var(--dark);
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.portfolio-metrics {
  display: flex;
  gap: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--light-gray);
}

.metric-small {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dark);
}

.metric-small svg {
  stroke: var(--success);
}

/* Contact */
.contact {
  padding: 100px 0;
  background: #f8fafc;
}

.contact-wrapper {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 60px;
  align-items: start;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;
}

.contact-detail-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.contact-detail-item svg {
  flex-shrink: 0;
  stroke: var(--primary);
}

.contact-detail-item h4 {
  font-size: 14px;
  font-weight: 700;
  color: var(--dark);
  margin-bottom: 4px;
}

.contact-detail-item p {
  font-size: 15px;
  color: var(--gray);
}

.trust-badges {
  display: flex;
  gap: 20px;
}

.trust-badge {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  text-align: center;
}

.trust-badge svg {
  stroke: var(--success);
}

.trust-badge span {
  font-size: 13px;
  font-weight: 600;
  color: var(--dark);
}

.contact-form {
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.12);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--dark);
  font-size: 14px;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid var(--light-gray);
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.3s;
  background: white;
  color: var(--dark);
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.checkbox-group {
  margin-top: 24px;
}

.checkbox-label {
  display: flex;
  align-items: start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: var(--gray);
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin-top: 2px;
  cursor: pointer;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid var(--light-gray);
  border-radius: 6px;
  flex-shrink: 0;
}

.submit-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white;
  border: none;
  padding: 18px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 24px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.4);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-message {
  margin-top: 20px;
  padding: 16px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.submit-message.success {
  background: #d1fae5;
  color: #065f46;
}

.submit-message svg {
  flex-shrink: 0;
  stroke: #065f46;
}

/* Footer */
.footer {
  background: var(--dark);
  color: white;
  padding: 60px 0 30px;
}

.footer-content {
  display: grid;
  grid-template-columns: 1.5fr 2fr;
  gap: 60px;
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-logo {
  width: 50px;
  height: 50px;
}

.footer-brand-name {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.footer-brand-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}

.footer-column h4 {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
}

.footer-column a {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  margin-bottom: 12px;
  font-size: 14px;
  transition: color 0.3s;
}

.footer-column a:hover {
  color: white;
}

.footer-bottom {
  text-align: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-content {
    grid-template-columns: 1fr;
  }
  
  .hero-visual {
    display: none;
  }
  
  .services-container {
    grid-template-columns: 1fr;
  }
  
  .contact-wrapper {
    grid-template-columns: 1fr;
  }
  
  .footer-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
  }
  
  .mobile-menu-btn {
    display: flex;
  }
  
  .hero {
    padding: 80px 0 60px;
  }
  
  .hero-title {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .hero-stats {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .section-title {
    font-size: 32px;
  }
  
  .section-subtitle {
    font-size: 16px;
  }
  
  .usp-grid {
    grid-template-columns: 1fr;
  }
  
  .tech-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .portfolio-grid {
    grid-template-columns: 1fr;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .footer-links {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .footer-bottom {
    text-align: center;
  }
  
  .stats-widget {
    padding: 40px 0;
  }
  
  .widget-card {
    padding: 24px;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .metric {
    padding: 16px;
  }
  
  .metric-value {
    font-size: 24px;
  }
  
  .trust-badges {
    flex-direction: column;
  }
  
  .services-visual {
    order: 2;
  }
  
  .services-list {
    order: 1;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 16px;
  }
  
  .hero-title {
    font-size: 28px;
  }
  
  .section-title {
    font-size: 26px;
  }
  
  .tech-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .tech-logo {
    width: 80px;
    height: 80px;
  }
  
  .tech-icon {
    font-size: 24px;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .contact-form {
    padding: 24px;
  }
  
  .portfolio-card {
    margin: 0 -8px;
  }
  
  .usp-card {
    padding: 30px 20px;
  }
  
  .stat-number {
    font-size: 28px;
  }
  
  .hero-stats {
    padding: 20px 0;
  }
}

/* Additional Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-text,
.hero-visual {
  animation: fadeIn 0.8s ease-out;
}

.hero-visual {
  animation-delay: 0.2s;
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

/* Selection Styles */
::selection {
  background: var(--primary);
  color: white;
}

::-moz-selection {
  background: var(--primary);
  color: white;
}

/* Scrollbar Styles */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--secondary);
}

.submit-message.error {
  background: #fee2e2;
  color: #991b1b;
  border-left: 4px solid #dc2626;
}

.submit-message.error svg {
  stroke: #991b1b;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading States */
.submit-button:disabled svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Focus Visible for Accessibility */
button:focus-visible,
a:focus-visible,
input:focus-visible,
textarea:focus-visible,
select:focus-visible {
  outline: 3px solid var(--primary);
  outline-offset: 2px;
}

/* Print Styles */
@media print {
  .nav,
  .mobile-menu,
  .cta-button,
  .submit-button,
  .floating-card {
    display: none;
  }
  
  body {
    color: #000;
  }
  
  .hero-background {
    display: none;
  }
  
  .hero-text {
    color: #000;
  }
}

/* Accessibility - Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .grid-overlay {
    animation: none;
  }
  
  .floating-card {
    animation: none;
  }
}

/* Dark Mode Support (optional - falls gewünscht) */
@media (prefers-color-scheme: dark) {
  /* Kann aktiviert werden, wenn Dark Mode gewünscht ist */
  /* body {
    background: #0f172a;
    color: #e2e8f0;
  } */
}
</style>